// src/utils/aiAnalysisUtils.ts
// Pure utility functions for AI skin analysis processing
// Extracted from App.js - no React dependencies

import type { LanguageCode } from '../types';

/** Skin type codes from AI analysis */
export type SkinTypeCode = 0 | 1 | 2 | 3;

/** Skin type detail from AI analysis */
export interface SkinTypeDetail {
  area: number;
  oil_level?: number;
}

/** Skin type object from AI analysis */
export interface SkinTypeObject {
  skin_type: SkinTypeCode;
  details?: SkinTypeDetail[];
}

/** AI analysis result from AILabTools */
export interface AIAnalysisResult {
  pores_forehead?: {
    confidence: number;
    value: number;
  };
  pores_left_cheek?: {
    confidence: number;
    value: number;
  };
  pores_right_cheek?: {
    confidence: number;
    value: number;
  };
  pores_jaw?: {
    confidence: number;
    value: number;
  };
  acne?: {
    confidence: number;
    value: number;
  };
  blackhead?: {
    confidence: number;
    value: number;
  };
  skin_spot?: {
    confidence: number;
    value: number;
  };
  nasolabial_fold?: {
    confidence: number;
    value: number;
  };
  forehead_wrinkle?: {
    confidence: number;
    value: number;
  };
  eye_finelines?: {
    confidence: number;
    value: number;
  };
  crows_feet?: {
    confidence: number;
    value: number;
  };
  dark_circle?: {
    confidence: number;
    value: number;
  };
  eye_pouch?: {
    confidence: number;
    value: number;
  };
  left_eyelids?: {
    confidence: number;
    value: number;
  };
  right_eyelids?: {
    confidence: number;
    value: number;
  };
  glabella_wrinkle?: {
    confidence: number;
    value: number;
  };
  mole?: {
    confidence: number;
    value: number;
  };
  skin_type?: SkinTypeCode | SkinTypeObject;
  [key: string]: unknown;
}

/** Skin type label mapping */
const skinTypeMap: Record<SkinTypeCode, string> = {
  0: '지성',
  1: '건성',
  2: '중성',
  3: '복합성',
};

/**
 * Extract skin concerns from AI analysis result
 * @param result - AI analysis result object
 * @param lang - Language code ('en' or 'ko')
 * @returns Array of concern strings
 */
export const extractConcernsFromAnalysis = (result: AIAnalysisResult, lang: LanguageCode): string[] => {
  const concerns: string[] = [];

  if (result.pores_forehead?.value === 1 || result.pores_left_cheek?.value === 1 || result.pores_right_cheek?.value === 1 || result.pores_jaw?.value === 1) {
    concerns.push(lang === 'en' ? 'pores' : '모공');
  }
  if (result.acne?.value === 1) {
    concerns.push(lang === 'en' ? 'acne' : '여드름');
  }
  if (result.blackhead?.value === 1) {
    concerns.push(lang === 'en' ? 'blackheads' : '블랙헤드');
  }
  if (result.skin_spot?.value === 1) {
    concerns.push(lang === 'en' ? 'dark spots' : '색소침착');
  }
  if (result.nasolabial_fold?.value === 1 || result.forehead_wrinkle?.value === 1 || result.eye_finelines?.value === 1 || result.crows_feet?.value === 1) {
    concerns.push(lang === 'en' ? 'wrinkles' : '주름');
  }
  if (result.dark_circle?.value === 1) {
    concerns.push(lang === 'en' ? 'dark circles' : '다크서클');
  }
  if (result.eye_pouch?.value === 1) {
    concerns.push(lang === 'en' ? 'eye bags' : '눈밑지방');
  }

  return concerns;
};

/**
 * Map numeric skin-type code → Korean label for storage
 * @param skinTypeCode - Skin type code (0-3)
 * @param _lang - Language code (unused, always returns Korean for storage)
 * @returns Korean skin type label or null
 */
export const mapSkinTypeFromAI = (skinTypeCode: number | undefined, _lang: LanguageCode): string | null => {

  // Always return Korean for consistent storage
  const result = skinTypeCode !== undefined ? skinTypeMap[skinTypeCode as SkinTypeCode] || null : null;
  return result;
};

// Canonical concern aliases — normalize near-duplicates before dedup
// Includes Korean aliases so cross-language analyses deduplicate correctly
const CONCERN_ALIASES: Record<string, string> = {
  'enlarged pores': 'pores',
  'large pores': 'pores',
  'large-pores': 'pores',
  '모공': 'pores',
  'dark_spots': 'dark spots',
  'dark-spots': 'dark spots',
  '색소침착': 'dark spots',
  'dark_circles': 'dark circles',
  'dark-circles': 'dark circles',
  '다크서클': 'dark circles',
  'eye_bags': 'eye bags',
  'eye-bags': 'eye bags',
  'under-eye fat': 'eye bags',
  '눈밑지방': 'eye bags',
  '주름': 'wrinkles',
  '여드름': 'acne',
  '블랙헤드': 'blackheads',
};

const canonicalize = (concern: string): string => {
  const key = concern.trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
  return CONCERN_ALIASES[key] || key;
};

/**
 * Merge concerns arrays without duplicates, normalizing aliases
 * @param existing - Existing concerns array
 * @param detected - Newly detected concerns array
 * @returns Merged array without duplicates
 */
export const mergeConcerns = (existing: string[], detected: string[]): string[] => {
  const normalized = [...existing, ...detected].map(canonicalize);
  return [...new Set(normalized)];
};

/**
 * Generate bilingual search keywords from analysis result
 * @param result - AI analysis result object
 * @param lang - Language code ('en' or 'ko')
 * @returns Array of bilingual search keywords
 */
export const generateSearchKeywords = (result: AIAnalysisResult, lang: LanguageCode): string[] => {
  // Extract skin type value - handle both direct number and nested object
  const skinTypeValue: number | undefined =
    typeof result.skin_type === 'object'
      ? (result.skin_type as SkinTypeObject)?.skin_type
      : result.skin_type;


  const keywords: string[] = [];

  // Generate keywords in BOTH languages for better catalog matching
  const skinTypeKo = mapSkinTypeFromAI(skinTypeValue, 'ko');
  const skinTypeEn = mapSkinTypeFromAI(skinTypeValue, 'en');
  if (skinTypeKo) keywords.push(skinTypeKo);
  if (skinTypeEn) keywords.push(skinTypeEn);

  // Add concerns in both languages
  const concernsKo = extractConcernsFromAnalysis(result, 'ko');
  const concernsEn = extractConcernsFromAnalysis(result, 'en');
  keywords.push(...concernsKo, ...concernsEn);

  // Add product categories in both languages
  if (result.pores_forehead?.value === 1 || result.pores_left_cheek?.value === 1) {
    keywords.push('모공축소', 'pore minimizer', 'pore-care', 'pore');
  }
  if (result.nasolabial_fold?.value === 1 || result.forehead_wrinkle?.value === 1) {
    keywords.push('주름개선', 'anti-aging', 'wrinkle', 'firming');
  }
  if (result.dark_circle?.value === 1) {
    keywords.push('아이크림', 'eye cream', 'dark circle');
  }
  if (result.acne?.value === 1) {
    keywords.push('acne-care', 'acne', 'blemish');
  }
  return keywords;
};

/** Description function type for processAnalysisResult */
type DescriptionFn = (value: unknown) => string | null;

/**
 * Human-readable description of analysis result for prompting OpenAI
 * @param result - AI analysis result object
 * @param language - Language code ('en', 'ko', 'pt-BR', or 'es')
 * @returns Human-readable analysis summary
 */
export const processAnalysisResult = (result: AIAnalysisResult, language: LanguageCode): string => {
  const issues: string[] = [];
  // Use Korean descriptions only for 'ko'; all other languages (en, pt-BR, es) use English
  const isKo = language === 'ko';
  
  const descriptions: Record<string, DescriptionFn> = {
    pores_left_cheek: (value) =>
      value === 1
        ? isKo
          ? '왼쪽 뺨에 확장된 모공이 감지되었습니다.'
          : 'Enlarged pores on the left cheek detected.'
        : null,
    nasolabial_fold: (value) =>
      value === 1
        ? isKo
          ? '팔자 주름이 있습니다.'
          : 'Nasolabial folds are present.'
        : null,
    eye_pouch: (value) =>
      value === 1
        ? isKo
          ? '눈 밑 지방이 감지되었습니다.'
          : 'Eye bags detected.'
        : null,
    forehead_wrinkle: (value) =>
      value === 1
        ? isKo
          ? '이마 주름이 보입니다.'
          : 'Forehead wrinkles are visible.'
        : null,
    skin_spot: (value) =>
      value === 1
        ? isKo
          ? '피부 반점이 감지되었습니다.'
          : 'Skin spots detected.'
        : null,
    acne: (value) =>
      value === 1
        ? isKo
          ? '여드름이 있습니다.'
          : 'Acne present.'
        : null,
    pores_forehead: (value) =>
      value === 1
        ? isKo
          ? '이마에 확장된 모공이 감지되었습니다.'
          : 'Enlarged pores on the forehead detected.'
        : null,
    pores_jaw: (value) =>
      value === 1
        ? isKo
          ? '턱선에 확장된 모공이 감지되었습니다.'
          : 'Enlarged pores on the jawline detected.'
        : null,
    left_eyelids: (value) => {
      const types: Record<number, string> = {
        0: isKo ? '왼쪽 눈은 홑꺼풀입니다.' : 'Single eyelids on the left eye.',
        1: isKo ? '왼쪽 눈은 평행형 쌍꺼풀입니다.' : 'Parallel double eyelids on the left eye.',
        2: isKo ? '왼쪽 눈은 물결형 쌍꺼풀입니다.' : 'Scalloped double eyelids on the left eye.',
      };
      return types[value as number] || null;
    },
    eye_finelines: (value) =>
      value === 1
        ? isKo
          ? '눈 주위에 잔주름이 감지되었습니다.'
          : 'Fine lines around the eyes detected.'
        : null,
    dark_circle: (value) =>
      value === 1
        ? isKo
          ? '눈 밑 다크서클이 감지되었습니다.'
          : 'Dark circles under the eyes detected.'
        : null,
    crows_feet: (value) =>
      value === 1
        ? isKo
          ? '눈가에 까마귀 발 주름이 감지되었습니다.'
          : "Crow's feet wrinkles detected."
        : null,
    pores_right_cheek: (value) =>
      value === 1
        ? isKo
          ? '오른쪽 뺨에 확장된 모공이 감지되었습니다.'
          : 'Enlarged pores on the right cheek detected.'
        : null,
    blackhead: (value) =>
      value === 1
        ? isKo
          ? '블랙헤드가 감지되었습니다.'
          : 'Blackheads detected.'
        : null,
    glabella_wrinkle: (value) =>
      value === 1
        ? isKo
          ? '미간에 주름이 감지되었습니다.'
          : 'Wrinkles between the eyebrows detected.'
        : null,
    mole: (value) =>
      value === 1
        ? isKo
          ? '점이 있습니다.'
          : 'Moles present.'
        : null,
    right_eyelids: (value) => {
      const types: Record<number, string> = {
        0: isKo ? '오른쪽 눈은 홑꺼풀입니다.' : 'Single eyelids on the right eye.',
        1: isKo ? '오른쪽 눈은 평행형 쌍꺼풀입니다.' : 'Parallel double eyelids on the right eye.',
        2: isKo ? '오른쪽 눈은 물결형 쌍꺼풀입니다.' : 'Scalloped double eyelids on the right eye.',
      };
      return types[value as number] || null;
    },
    skin_type: (value) => {
      const skinTypeObj = value as SkinTypeObject;
      const types: Record<number, string> = {
        0: isKo ? '지성 피부로 감지되었습니다.' : 'Oily skin type detected.',
        1: isKo ? '건성 피부로 감지되었습니다.' : 'Dry skin type detected.',
        2: isKo ? '중성 피부로 감지되었습니다.' : 'Neutral skin type detected.',
        3: isKo ? '복합성 피부로 감지되었습니다.' : 'Combination skin type detected.',
      };
      const skinTypeDescription = types[skinTypeObj?.skin_type] || null;

      if (skinTypeDescription && skinTypeObj?.details && skinTypeObj.details.length > 0) {
        const detailDescriptions = skinTypeObj.details.map((detail) => {
          const areas: Record<number, string> = {
            0: isKo ? '이마' : 'Forehead',
            1: isKo ? '왼쪽 뺨' : 'Left cheek',
            2: isKo ? '오른쪽 뺨' : 'Right cheek',
            3: isKo ? '턱' : 'Chin',
          };
          const area = areas[detail.area] || (isKo ? '알 수 없는 부위' : 'Unknown area');
          const oilLevel =
            detail.oil_level !== undefined
              ? detail.oil_level
              : isKo
              ? '알 수 없는 유분 수준'
              : 'Unknown oil level';
          return `${area}: Oil level ${oilLevel}`;
        });
        return `${skinTypeDescription} ${detailDescriptions.join(', ')}.`;
      }

      return skinTypeDescription;
    },
  };

  for (const [key, value] of Object.entries(result)) {
    // key should be like "eye_pouch", value should be like { value: 1, confidence: 0.9 }
    const descFn = descriptions[key];
    if (descFn && typeof value === 'object') {
      const description = descFn((value as any).value);
      if (description) {
        issues.push(description);
      }
    }
  }

  // Explicit language enforcement so the model doesn't follow prior conversation language
  const langEnforcement: Record<string, string> = {
    en: '\n\nIMPORTANT: You MUST respond ONLY in English.',
    ko: '\n\nIMPORTANT: 반드시 한국어로만 답변하세요.',
    'pt-BR': '\n\nIMPORTANT: Responda apenas em português.',
    es: '\n\nIMPORTANT: Responde solo en español.',
  };
  const enforce = langEnforcement[language] || langEnforcement.en;

  return issues.length
    ? language === 'ko'
      ? `[사진 분석 완료] 주요 발견: ${issues.join('; ')}.

최대 3-4문장으로 간결하게 답변하세요. 의학적이지 않고 친근하게.
다음 형식으로 답변:
1. 피부 상태 인정 (이름을 알면 사용)
2. 우선 관리가 필요한 2-3가지 핵심 사항
3. 마지막에: "[주요 고민]에 도움이 되는 제품을 찾아볼게요!" - 자동 제품 검색이 바로 시작됨을 알림

모든 기술적 발견을 나열하지 마세요. 실행 가능한 인사이트와 제품 발견으로 유도하세요.${enforce}`
      : `[Photo Analysis Complete] Key findings: ${issues.join('; ')}. 
         
Respond in 3-4 sentences maximum. Be warm and friendly, not clinical. 
Format your response as:
1. One sentence acknowledging their skin (use their name if known)
2. Top 2-3 priority concerns to address
3. End with: "Let me find some products that can help with [top concern]!" - Product search starts automatically after this message.

Do NOT list all technical findings. Focus on actionable insights and guiding them toward product discovery.${enforce}`
    : language === 'ko'
    ? `사진 분석 완료 - 피부가 건강해 보여요! 피부 상태를 유지하는 데 도움이 되는 제품을 찾아볼게요!${enforce}`
    : `Photo analysis complete - your skin looks healthy! Let me find some products to help maintain your skin condition!${enforce}`;
};
